import ReCAPTCHA from 'react-google-recaptcha';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Checkbox, CircularProgress, Grid, Typography, useMediaQuery } from '@mui/material';
import { ChangeEvent, useRef, useState } from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { Alert } from '../../../../../../shared/lib/common/Alert';
import { CustomController } from '../../../../../../shared/lib/common/CustomController';
import { PageHeading } from '../../../../../../shared/lib/common/PageHeading';
import { SocialSource, useRegisterMutation } from '../../../../generated';
import { customTheme } from '../../../../../../shared/lib/theme';
import palette from '../../../../../../shared/lib/theme/palette';
import { LinkStyle, authBackdropStyle, authCardScrollWrapper, authCardWideStyle, authCardWrapper } from '../../../../../../shared/lib/theme/styleConstants';
import { BUTTON_TEXT_LOGIN, BUTTON_TEXT_REGISTER, TEXT_ALREADY_HAVE_ACCOUNT } from '../../../../../../shared/lib/constants';
import { AuthContainer } from '../../../../components/auth/AuthContainer';
import Verify from '../../../../components/auth/Verify';
import { LINKS, MSG_PLEASE_VERIFY_CAPTCHA, SIGN_UP_FIELDS, PAGE_TITLE_SIGNUP_PORTAL, PAGE_SUBTITLE_SIGNUP_PORTAL } from '../../../constants';
import { SignUpInput } from '../../../interfaceTypes';
import { signUpValidationSchema } from '../../../validationSchema';
import SocialSourceSelector from '../../../../components/SocialSourceSelector';
import PendingPromoAlert from '../../../../components/common/PendingPromoAlert';
import { useMobileView } from '../../../hooks/useMobileView';

const SignUp = () => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [email, setEmail] = useState<string>('');
  const [verify, setVerify] = useState<boolean>(false);
  const mediumViewport = useMobileView();
  const smallViewport = useMediaQuery('(max-width:394px)');
  const [checked, setChecked] = useState<boolean>(false);
  const [socialSource, setSocialSource] = useState<SocialSource | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const {
    blackOne,
    blackTwo,
    primary: { main: PrimaryMain }
  } = palette;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleOpen = () => {
    window.open(`/portal${LINKS.TERMS_AND_CONDITIONS_LINK}`, '_blank', 'noopener,noreferrer');
  };

  const handlePolicy = () => {
    window.open(`/portal${LINKS.PRIVACY_POLICY_LINK}`, '_blank', 'noopener,noreferrer');
  };

  const methods = useForm<SignUpInput, any, SignUpInput>({
    mode: 'all',
    resolver: yupResolver(signUpValidationSchema),

    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      passwordAgain: ''
    }
  });

  const [signUp, { loading }] = useRegisterMutation({
    onCompleted() {
      setVerify(true);
    },
    onError({ message }: { message: string }) {
      Alert.error(message);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  });

  const {
    handleSubmit,
    formState: { isValid, isSubmitting }
  } = methods;

  const onSubmit: SubmitHandler<SignUpInput> = async (data) => {
    if (!captchaToken) {
      Alert.error(MSG_PLEASE_VERIFY_CAPTCHA);
      return;
    }

    const { firstName, lastName, password, email } = data;
    setEmail(email);
    await signUp({
      variables: {
        registerUserInput: {
          email,
          fullName: `${firstName} ${lastName}`,
          password,
          emailVerified: false,
          socialSourceId: socialSource?.id || null,
          captchaToken
        }
      }
    });
  };

  return (
    <AuthContainer>
      <Box sx={authBackdropStyle} />

      {!verify ? (
        <Box flex={mediumViewport ? 'none' : 1} sx={authCardScrollWrapper}>
          <Box sx={authCardWideStyle}>
            <PendingPromoAlert />

            <PageHeading color={blackOne} subTitle={PAGE_SUBTITLE_SIGNUP_PORTAL} title={PAGE_TITLE_SIGNUP_PORTAL} />

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <Grid container spacing={2}>
                  {SIGN_UP_FIELDS.map((field) => {
                    const { fieldType, name, title, xs, md } = field;

                    return (
                      <Grid item xs={xs} md={md} key={`${name}-${title}`}>
                        <CustomController controllerName={name} controllerLabel={title} fieldType={fieldType} />
                      </Grid>
                    );
                  })}
                </Grid>

                <Box mt={2} mb={2}>
                  <SocialSourceSelector
                    value={socialSource}
                    onChange={(val) => {
                      setSocialSource(val);
                    }}
                  />
                </Box>

                <Box display="flex" justifyContent="start" alignItems="flex-start">
                  <Checkbox sx={{ paddingLeft: '0', paddingRight: smallViewport ? '5px' : '9px' }} checked={checked} onChange={handleChange} inputProps={{ 'aria-label': 'controlled' }} />

                  <Typography
                    variant="subtitle2"
                    marginTop={smallViewport ? '2px' : '9px'}
                    sx={{
                      marginBottom: 2,
                      color: blackTwo,
                      [customTheme.breakpoints.down(394)]: {
                        fontSize: '12px'
                      }
                    }}
                  >
                    Please click here to agree to the
                    <Box component="span" onClick={handleOpen} sx={LinkStyle}>
                      Terms and Conditions
                    </Box>
                    and
                    <Box component="span" onClick={handlePolicy} sx={LinkStyle}>
                      Privacy Policy.
                    </Box>
                  </Typography>
                </Box>

                <ReCAPTCHA ref={recaptchaRef} sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''} onChange={(token) => setCaptchaToken(token)} />

                <Button
                  sx={{ marginTop: 2 }}
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={loading || !checked || !isValid || isSubmitting || !captchaToken}
                  endIcon={loading && <CircularProgress size={20} color="inherit" />}
                >
                  {BUTTON_TEXT_REGISTER}
                </Button>
              </form>
            </FormProvider>

            <Box display="flex" justifyContent="center" my={2}>
              <Typography sx={{ color: blackOne }} display="flex" alignItems="center" variant="subtitle2">
                {TEXT_ALREADY_HAVE_ACCOUNT}
                <Link to="/login">
                  <Typography variant="subtitle2" sx={{ color: PrimaryMain, fontWeight: '700', marginLeft: '5px' }}>
                    {BUTTON_TEXT_LOGIN}
                  </Typography>
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box flex={mediumViewport ? 'none' : 1} sx={authCardWrapper}>
          <Box sx={authCardWideStyle}>
            <Verify email={email} />
          </Box>
        </Box>
      )}
    </AuthContainer>
  );
};

export default SignUp;
